import { Alert, Button, Form, Modal, Typography, notification } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import { getAllSchools } from "store/slices/schoolsSlice";
import {
  getStudentById,
  resetStudentPasswordByAdmin,
  updateStudentById,
} from "store/slices/studentsSlice";
import StudentForm from "./student-form";

const extractTemporaryPassword = (payload) =>
  payload?.temporaryPassword ||
  payload?.tempPassword ||
  payload?.newPassword ||
  payload?.password ||
  payload?.data?.temporaryPassword ||
  payload?.data?.tempPassword ||
  payload?.data?.newPassword ||
  payload?.data?.password ||
  "";

const EditStudentModal = () => {
  const { Text } = Typography;
  const [form] = Form?.useForm();
  const dispatch = useDispatch();
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { loading, student } = useSelector((state) => state?.students);
  const { allSchools } = useSelector((state) => state?.schools);
  const open = isModalOpen && modalType === "edit_student";
  const studentId = modalProps?.studentId;
  const { t } = useTranslation();
  const [isMinor, setIsMinor] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const studentInfos = student?.student;

  useEffect(() => {
    if (open && studentId) {
      dispatch(getAllSchools());
      dispatch(getStudentById(studentId));
    }
  }, [dispatch, open, studentId]);

  useEffect(() => {
    if (open && studentInfos) {
      if (studentInfos?.birthDate) {
        const age = dayjs()?.diff(dayjs(studentInfos?.birthDate), "years");
        setIsMinor(age < 18);
      }

      form?.setFieldsValue({
        ...studentInfos,
        birthDate: dayjs(studentInfos?.birthDate),
      });
    }
  }, [open, studentInfos, allSchools, form]);

  const handleFormValuesChange = (changedValues) => {
    if (changedValues?.birthDate) {
      const age = dayjs()?.diff(dayjs(changedValues?.birthDate), "years");
      setIsMinor(age < 18);
    }
  };

  const onSubmit = async (values) => {
    if (values?.birthDate) {
      values.birthDate = dayjs(values?.birthDate)?.format("YYYY-MM-DD");
    }

    delete values.password;
    delete values.confirmPassword;

    return await dispatch(
      updateStudentById({
        studentId,
        data: values,
      })
    )
      ?.unwrap()
      ?.then(() => {
        notification?.success({
          message: "Success",
          description: t("students.crud.form.success"),
        });
        onClose();
      })
      ?.catch((error) => {
        notification?.error({
          message: error || "Error",
          description: error || t("students.crud.form.error.failed"),
        });
      });
  };

  const handleResetPassword = async () => {
    return await dispatch(resetStudentPasswordByAdmin(studentId))
      ?.unwrap()
      ?.then(async (payload) => {
        setTemporaryPassword(extractTemporaryPassword(payload));
        await dispatch(getStudentById(studentId));
        notification?.success({
          message: t(
            "students.crud.form.passwordReset.generated.title",
            "Password reset done"
          ),
          description: t(
            "students.crud.form.passwordReset.generated.description",
            "Temporary password has been generated successfully."
          ),
        });
      })
      ?.catch((error) => {
        notification?.error({
          message: error || "Error",
          description:
            error ||
            t(
              "students.crud.form.passwordReset.error",
              "Unable to reset password."
            ),
        });
      });
  };

  const onClose = () => {
    form?.resetFields();
    setTemporaryPassword("");
    dispatch(closeModal());
  };

  return (
    <Modal
      title={<h4 style={{ textAlign: "center" }}>{t("students.crud.edit")}</h4>}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("students.form.button.cancel")}
        </Button>,
        <Button key="reset-password" onClick={handleResetPassword} disabled={loading}>
          {t(
            "students.form.button.resetPassword",
            "Reinitialiser le mot de passe"
          )}
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          form="student_form"
          disabled={loading}
        >
          {t("students.form.button.save")}
        </Button>,
      ]}
      centered
      styles={{ content: { overflowY: "auto", maxHeight: "75vh" } }}
    >
      {temporaryPassword && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t(
            "students.crud.form.passwordReset.temporaryPassword.title",
            "Temporary password"
          )}
          description={
            <div>
              <div>
                {t(
                  "students.crud.form.passwordReset.temporaryPassword.description",
                  "Share this temporary password with the student if needed."
                )}
              </div>
              <Text code>{temporaryPassword}</Text>
            </div>
          }
        />
      )}
      <StudentForm
        form={form}
        schools={allSchools}
        onSubmit={onSubmit}
        isEditing={true}
        isMinor={isMinor}
        onValuesChange={handleFormValuesChange}
      />
    </Modal>
  );
};

export default EditStudentModal;
